import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import {
    IAccommodation,
    IGetBookingsAirResponse,
    IGetBookingsStayResponse,
    IPrice,
    ITraveller,
    IXId,
    TGetBookingResponse
} from 'libraries/service/resources/booking/interfaces/IGetBookings';
import { ISummarySectorAir, ISummarySectorStay } from './components/shared/interfaces/ISummary';
import { Yii } from 'libraries/interfaces/Yii';
import { ILabels } from 'libraries/interfaces/ILabels';
import { CardComponent } from 'libraries/ui_card/card.component';
import { IMenuDetails } from 'libraries/hotel/models/menu-details';
import { MenuDetailsService } from 'libraries/hotel/components/menu-details/services/menu-details.service';
import { IFieldSpecs } from 'libraries/module/formbuilder/fields/interfaces/field-specs';
import { IMultiselectConfig, IMultiselectOptions } from 'libraries/module/formbuilder/fields/multiselect/multiselect.model';
import { INameXId, IServicesListGetterInput, ITableSectorServices } from './components/shared/services/table-services/table-services.service';
import { SectorService } from 'libraries/service/sector/sector.service';
import { IServicesConfig } from './components/shared/interfaces/ISectorServicesConfig';
import { IGetEnumsService } from 'libraries/service/booking/enums/IGetEnums';
import { MatDialog } from '@angular/material/dialog';
import { SectorAirDeleteDialogComponent } from './components/sectors-air/sector-air-delete-dialog/sector-air-delete-dialog.component';
import { SectorAirGroupService } from "./components/sectors-air/sector-air-group/sector-air-group.service";
import { PermissionResourceService } from 'libraries/service/resources/permission/permissionResource.service';
import { NotificationType } from 'libraries/interfaces/INotification';
import { IDeleteSectorsRequest } from 'libraries/service/resources/model/booking/air/IAirCreateBooking';
import {BookingAirToolsService} from "../../services/booking-air-tools.service";


interface ITableServices {
    air: ITableSectorServices;
    stay: ITableSectorServices;
}

@Component({
    selector: 'sectors',
    template: `
        <card #cardContainer [isCollapsible]="true">
            <div class="card-header-content" >
                <div class="title" (click)="menuToggleCard()">
                    {{ cardLabels.title }}
                </div>

                <div *ngIf="cardContainer.showContent && detailsIsShown" class="tabs-field">
                    <menu-details [items]="menuItems" (onChangeTab)="changeTab($event)"></menu-details>
                </div>

                <btn *ngIf="showBookingActions && showDeleteSectorAction && detailsIsShown && tab === 'details'"
                     [label]="tabLabels.deleteSectors"
                     class="delete-sectors-link"
                     type="actionDo"
                     [disableIf]="sectorsChecked.length===0"
                     (onClick)="showDeleteConfirmDialog()">
                </btn>
                <btn *ngIf="cardContainer.showContent"
                     [label]="toggleCardDetailsLabel()"
                     class="toggle-details-link"
                     classList="no-spacing"
                     type="actionHeaderLink"
                     (click)="detailsIsShown = !detailsIsShown">
                </btn>
            </div>

            <div class="card-sub-header-content" *ngIf="detailsIsShown">
                <div class="filter-container">
                    <div class="filter-traveller">
                        <multiselect [fieldConfig]="filtersFieldsConfig.traveller"
                                     [options]="filtersOptions.traveller"
                                     [labelCustom]="filtersConfig.traveller.defaultLabel"
                                     [disabled]="filtersOptions.traveller.length < 2">
                        </multiselect>
                    </div>
                    <div class="filter-services" *ngIf="tab === 'services'">
                        <multiselect [fieldConfig]="filtersFieldsConfig.service"
                                     [options]="filtersOptions.service"
                                     [labelCustom]="filtersConfig.service.defaultLabel"
                                     [disabled]="filtersOptions.service.length < 2">
                        </multiselect>
                    </div>
                </div>
                <div *ngIf="showBookingActions && showDeleteSectorAction && tab === 'details'" class="toggleAllSegment">
                    <label for="toggleAllSegment">{{isAllSectorChecked ? tabLabels.uncheckedAllSectors : tabLabels.checkedAllSectors}}:</label>
                    <input id="toggleAllSegment" type="checkbox" [checked]="isAllSectorChecked" (input)="updateAllSectorChecked()">
                </div>
            </div>
            <div class="card-body-content content">
                <div class="booking-data-content" *ngIf="sectors.length; else noSectorDefined"
                     [class.details-closed]="!detailsIsShown">
                    <div *ngFor="let sector of sectors" class="sections" [attr.id]="'sector_' + sector.xId">
                        <sectors-stay *ngIf="sector.xType === 'stay' && filterableTableServices"
                                      [sector]="sector"
                                      [booking]="filterableBooking"
                                      [view]="tab"
                                      [detailsIsShown]="detailsIsShown"
                                      [tableServices]="filterableTableServices?.stay">
                        </sectors-stay>

                        <sectors-air *ngIf="sector.xType === 'air' && filterableTableServices"
                                     [sector]="sector"
                                     [sectorsChecked]="sectorsChecked"
                                     [booking]="filterableBooking"
                                     [showBookingActions]="showBookingActions"
                                     [showDeleteSectorAction]="showDeleteSectorAction"
                                     [view]="tab"
                                     [detailsIsShown]="detailsIsShown"
                                     [tableServices]="filterableTableServices.air"
                                     [serviceEnum]="serviceEnum">
                            
                        </sectors-air>
                    </div>
                </div>
                <ng-template #noSectorDefined>
                    <notification type="error">{{ tabLabels.noSectorDefined }}</notification>
                </ng-template>
            </div>
        </card>
    `,
    styleUrls: ['../card.scss']
})
export class SectorsComponent implements OnChanges, OnInit, OnDestroy {

    @Input()
    public readonly booking: TGetBookingResponse;
    public filterableBooking: TGetBookingResponse;

    @Input()
    public readonly tableServices: ITableServices;
    public filterableTableServices: ITableServices;

    @Input()
    public serviceEnum: IGetEnumsService[];

    @Input()
    public showBookingActions: boolean = true;

    @Output()
    public readonly onCardTitleClick: EventEmitter<string> = new EventEmitter();

    @ViewChild('cardContainer')
    public cardContainer: CardComponent;

    public sectors: ISummarySectorStay[] | ISummarySectorAir[];

    public notificationType: typeof NotificationType = NotificationType;

    public filterFormGroup: FormGroup;

    public filtersFieldsConfig: {
        traveller: IFieldSpecs,
        service: IFieldSpecs,
    };

    public filtersConfig: {
        traveller: IMultiselectConfig,
        service: IMultiselectConfig,
    };

    public filtersOptions: {
        traveller: IMultiselectOptions[],
        service: IMultiselectOptions[],
    };


    public tabLabels: ILabels = {
        details: Yii.t('wui-shared-js', 'Détails'),
        services: Yii.t('wui-shared-js', 'Services'),
        noSectorDefined: Yii.t('wui-shared-js', "Aucune étape n'est définie sur ce dossier"),
        deleteSectors: Yii.t('wui-shared-js', 'Supprimer'),
        checkedAllSectors: Yii.t('wui-shared-js', 'Tout cocher'),
        uncheckedAllSectors: Yii.t('wui-shared-js', 'Tout dé-cocher'),
    };

    public cardLabels: ILabels = {
        title: Yii.t('wui-shared-js', 'Étapes'),
        hideDetails: Yii.t('wui-shared-js', 'Cacher les détails'),
        showDetails: Yii.t('wui-shared-js', 'Montrer les détails'),
    };

    public detailsIsShown: boolean = true;

    public tab: string = 'details';

    public name: string = 'sectors';

    public menuItems: IMenuDetails[] = [
        {
            code: 'details',
            name: this.tabLabels.details,
            exist: true,
            selected: true
        },
        {
            code: 'services',
            name: this.tabLabels.services,
            exist: true,
            selected: false
        }
    ];

    public hasSectors: boolean;
    public sectorsChecked: ISummarySectorAir[] = [];
    public isAllSectorChecked: boolean = false;
    public deleteSegmentPermission: boolean = false;
    public showDeleteSectorAction: boolean = false;
    private subscription: Subscription = new Subscription();
    private updateSectorsCheckedSubscription: Subscription;

    constructor(
        private readonly dialog: MatDialog,
        private readonly menuDetailsService: MenuDetailsService,
        private readonly formBuilder: FormBuilder,
        private readonly sectorService: SectorService,
        private readonly sectorAirGroupService: SectorAirGroupService,
        private readonly permissionResource: PermissionResourceService,
        private readonly bookingAirToolsService: BookingAirToolsService,
    ) {
    }

    public async ngOnInit(): Promise<void> {
        this.initSectorsChecked(this.sectorAirGroupService.sectorXIds);
        await this.permissionResource.getPermissions().toPromise()
            .then((permissions: Set<string>): void => {
                this.deleteSegmentPermission = permissions.has('shared.air.booking.segment.delete');
            });
        this.initShowDeleteSectorAction();
        if (this.showDeleteSectorAction) {
            this.updateSectorsCheckedSubscription = this.sectorAirGroupService.updateSectorsCheckedObs
                .subscribe((sectors:  string[]): void => {
                    this.initSectorsChecked(sectors);
                });
        }
    }

    public ngOnChanges(): void {
        this.filterableBooking = this.booking;
        this.filterableTableServices = this.tableServices;

        this.sectors = this.sectorService.getSectors(this.booking);
        this.initFilters();

        this.hasSectors = Boolean(this.booking && (this.booking as IGetBookingsStayResponse | IGetBookingsAirResponse).sector && (this.booking as IGetBookingsStayResponse | IGetBookingsAirResponse).sector.length);

        if (!this.hasSectors) {
            this.detailsIsShown = false;
        }
    }

    public ngOnDestroy(): void {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
        if (this.updateSectorsCheckedSubscription) {
            this.updateSectorsCheckedSubscription.unsubscribe();
        }
    }

    private initSectorsChecked(sectors: string[]): void {
        if (sectors.length === 0) {
            this.isAllSectorChecked = false;
            this.sectorsChecked = [];
        } else {
            this.sectorsChecked = (this.sectors as ISummarySectorAir[])
                .filter((sector: ISummarySectorAir): boolean => (sector.xId && sectors.includes(sector.xId)))
            this.isAllSectorChecked = sectors.length === this.sectors.length;
            // if xIds change with update and one xId don't match reset all
            if (sectors.length !== this.sectorsChecked.length) {
                this.sectorAirGroupService.updateSectorXIds(null);
                this.isAllSectorChecked = false;
            } else {
                this.isAllSectorChecked = sectors.length === this.sectors.length;
            }
        }
    }

    // check if category not lowcost and has permission and isAirSector on same property
    private initShowDeleteSectorAction(): void {
        // IsAirSector() method this.sectors[0].xType === 'air' add in check to show button ?
        const isOneSectorLowCost: boolean = (this.sectors as ISummarySectorAir[])
            .some((sector: ISummarySectorAir): boolean => !sector.category || sector.category === 'lowcost' || sector.xType !== 'air');
        this.showDeleteSectorAction = !isOneSectorLowCost && this.deleteSegmentPermission && this.booking.issueStatus === 'notIssued';
    }

    public toggleCardDetailsLabel(): string {
        return this.detailsIsShown ? this.cardLabels.hideDetails : this.cardLabels.showDetails;
    }

    public changeTab(tabCode: string): void {
        this.isAllSectorChecked=false;
        this.sectorAirGroupService.updateSectorXIds(null);
        this.menuDetailsService.changeTab(this.menuItems, tabCode);
        this.tab = tabCode;
    }

    public menuToggleCard(): void {
        this.onCardTitleClick.emit(this.name);
    }

    private initFilters(): void {
        this.filtersOptions = getFiltersOptions(this.booking, this.tableServices);
        this.filterFormGroup = initFiltersFormGroup(this.formBuilder, this.filtersOptions);
        this.filtersFieldsConfig = {
            traveller: {
                formGroup: this.filterFormGroup,
                formControlName: "traveller",
            },
            service: {
                formGroup: this.filterFormGroup,
                formControlName: "service",
            }
        };
        this.filtersConfig = {
            traveller: {
                defaultLabel: Yii.t('wui-shared-js', 'Filtrer sur les voyageurs'),
                displaySelectedLabel: false,
            },
            service: {
                defaultLabel: Yii.t('wui-shared-js', 'Filtrer sur les services'),
                displaySelectedLabel: false,
            }
        };

        this.subscription = this.filterFormGroup.valueChanges
            .subscribe({
                next: (filterValue: { traveller: string[]; service: string[] }): void => {
                    const traveller: ITraveller[] = (this.booking.traveller || [])
                        .filter((travellerItem: ITraveller) => filterValue.traveller && filterValue.traveller.includes(travellerItem.xId));
                    if (this.filterableBooking.type && this.filterableBooking.type.some((type: string) => type === 'stay')) {
                        const booking: IGetBookingsStayResponse = this.booking as IGetBookingsStayResponse;
                        //to be sure to show old bookings
                        const accomodationArray: IAccommodation[] = !Array.isArray(booking.accomodation) ?
                            [booking.accomodation] :
                            booking.accomodation;
                        const accomodation: IAccommodation[] = accomodationArray
                            .filter((accomodationItem: IAccommodation) => {
                                return traveller.some((travellerItem: ITraveller) => {
                                    if (accomodationItem.traveller) {
                                        return accomodationItem.traveller.some((travellerXId: IXId) => {
                                            return travellerXId.xId === travellerItem.xId;
                                        });
                                    }
                                });
                            });
                        this.filterableBooking = {
                            ...this.booking,
                            accomodation,
                            traveller
                        };
                    } else {
                        this.filterableBooking = {
                            ...this.booking,
                            traveller,
                        };
                    }
                    this.filterableTableServices = filterServices(filterValue, this.tableServices, this.booking);
                }
            });
    }

    public showDeleteConfirmDialog(): void {
        if (this.sectorsChecked.length > 0) {
            this.dialog.open(SectorAirDeleteDialogComponent, {
                width: '650px',
                data: {
                    title: Yii.t('wui-sasha-js', 'Suppression de segments'),
                    question: Yii.t('wui-sasha-js', 'Confirmez-vous la suppression des segments suivants ?'),
                    cancel: Yii.t('wui-sasha-js', 'Abandonner'),
                    accept: Yii.t('wui-sasha-js', 'Supprimer'),
                    hasServices: this.bookingAirToolsService.hasServices(this.booking as IGetBookingsAirResponse),
                    sectors: this.sectorsChecked,
                    deleteSectorsRequest: this.makeSectorsDeleteRequest()
                }
            });
        }
    }

    public updateAllSectorChecked(): void {
        this.isAllSectorChecked = !this.isAllSectorChecked;
        if (this.isAllSectorChecked) {
            const sectors: string[] = (this.sectors as ISummarySectorAir[]).map((sector: ISummarySectorAir): string => {
                return sector.xId;
            });
            this.sectorAirGroupService.updateSectorXIds(sectors);
        } else {
            this.sectorAirGroupService.updateSectorXIds(null);
        }
    }

    private makeSectorsDeleteRequest(): IDeleteSectorsRequest {
        const deleteSectorsRequest: IDeleteSectorsRequest = {
            host: this.booking.host,
            booking: {
                syscode: this.booking.syscode,
                host: this.booking.host,
                segmentArray: []
            },
            taskArray: [{
                purpose: 'Delete',
                code: 'Segment',
                argumentArray: []
            }, {
                purpose: 'Delete',
                code: 'Price',
                argumentArray: []
            }]
        };
        this.sectorsChecked.map((sector: ISummarySectorAir): void => {
            deleteSectorsRequest.booking.segmentArray.push({ xId: sector.xId, key: sector.key });
            deleteSectorsRequest.taskArray[0].argumentArray.push({ref: sector.xId, role: 'Delete'});
        });
        return deleteSectorsRequest;
    }
}

function initFiltersFormGroup(fb: FormBuilder, options: { traveller: IMultiselectOptions[]; service: IMultiselectOptions[] }): FormGroup {
    return fb.group({
        traveller: fb.control(options.traveller.map((travellerOption: IMultiselectOptions) => travellerOption.value)),
        service: fb.control(options.service.map((serviceOption: IMultiselectOptions) => serviceOption.value))
    });
}

function getFiltersOptions(
    booking: TGetBookingResponse,
    tableServices: ITableServices
): {
    traveller: IMultiselectOptions[],
    service: IMultiselectOptions[],
} {
    const traveller: IMultiselectOptions[] = (booking.traveller || [])
        .map((travellerItem: ITraveller) => ({
            label: `${travellerItem.firstName} ${travellerItem.lastName}`,
            value: travellerItem.xId
        }));
    const service: IMultiselectOptions[] = getServicesFiltersOptions(tableServices);
    return {
        traveller,
        service
    };
}

function getServicesFiltersOptions(tableServices: ITableServices): IMultiselectOptions[] {
    return Object.keys(tableServices || [])
        .reduce((acc: IMultiselectOptions[], tableServicesKey: string) => ([
            ...acc,
            ...(tableServices[tableServicesKey] ?
                tableServices[tableServicesKey].servicesList.map((service: IServicesListGetterInput) => ({
                    label: tableServicesKey === 'stay' ? service.name : service.code,
                    value: service.code,
                })
                ) :
                []
            )
        ]), [])
        .filter((ssr: IMultiselectOptions, index: number, ssrArray: IMultiselectOptions[]) =>
            index === ssrArray.findIndex((ssrItem: IMultiselectOptions) => (ssrItem.value === ssr.value))
        );
}

function filterServices(
    { traveller, service }: { traveller: string[], service: string[] },
    tableServices: ITableServices,
    bookingData: TGetBookingResponse
): ITableServices {
    return Object.entries(tableServices || []).reduce(
        (acc: ITableServices, [tableServicesKey, tableServicesValue]: [string, ITableSectorServices]) => ({
            ...acc,
            [tableServicesKey]: {
                ...tableServicesValue,
                servicesList: (tableServicesValue ? (tableServicesValue.servicesList || []).filter(
                    (serviceItem: IServicesListGetterInput) => service.some((filteredService: string) => filteredService === serviceItem.code)
                ) : null),
                listIterator: (tableServicesValue ? (tableServicesValue.listIterator || []).filter(
                    (iterator: INameXId) => {
                        if (tableServicesKey === 'air') {
                            return traveller.some((filteredTraveller: string) => filteredTraveller === iterator.xId);
                        }
                        return isRoomIteratorfiltered(traveller, iterator, bookingData as IGetBookingsStayResponse);
                    }
                ) : null),
                servicesConfig: (tableServicesValue ? (Object.keys(tableServicesValue.servicesConfig) || [])
                    .reduce(
                        (servicesConfigAcc: IServicesConfig, serviceConfigKey: string) => ({
                            ...servicesConfigAcc,
                            ...(service.some((filteredService: string) => filteredService === serviceConfigKey) ?
                                { [serviceConfigKey]: tableServicesValue.servicesConfig[serviceConfigKey] } :
                                null)
                        }), {}) : null)
            },
        }), { air: null, stay: null }
    );
}

function isRoomIteratorfiltered(filteredTravellers: string[], iterator: INameXId, bookingData: IGetBookingsStayResponse): boolean {
    //to be sure to show old bookings
    const accomodations: IAccommodation[] = Array.isArray(bookingData.accomodation) ? bookingData.accomodation : [bookingData.accomodation];

    return (filteredTravellers || []).some((filteredTraveller: string) => accomodations
        .find((acco: IAccommodation) => acco.xId === iterator.xId).traveller
        .some((traveller: IXId) => traveller.xId === filteredTraveller)
    );
}
